/**
 * Videos on the Pixelmojo YouTube channel (@pixelmojohq).
 *
 * One source for every surface that names a video: the /about embed and its
 * VideoObject, the blog YouTubeVideo block, the newsletter welcome email,
 * llms.txt and llms-full.txt. Titles, lengths and upload dates are YouTube's
 * own (watch page, checked 2026-09-28 to 2026-09-30). Transcripts are the
 * videos' English captions (product names capitalized, spelling normalized).
 * A video with no dialogue carries its caption track: on-screen text and
 * [bracketed] sound cues.
 * Summaries and notes restate the video descriptions; never add a claim the
 * video or its description does not make.
 *
 * To add a video: copy its title, lengthSeconds and uploadDate from the watch
 * page, and its transcript from the captions.
 */

import type { VideoItem } from '@/components/seo/AutoSchema'

export interface YouTubeVideo {
  id: string
  title: string
  /** A Short is a vertical cut of the long-form video named in `shortOf`. */
  kind: 'video' | 'short'
  shortOf?: string
  /** Long-form only. Its page on the site: /videos/<slug>. Never change a
   *  published slug; the page URL is in the sitemap and llms.txt. */
  slug?: string
  /** YouTube's uploadDate, verbatim. */
  uploadDate: string
  durationSeconds: number
  /** Site page the video belongs with. */
  relatedPath: string
  /** Long-form only. */
  summary?: string
  /** Disclosure carried from the video description. */
  note?: string
  /** Long-form only. */
  transcript?: string
  /** Sources for evidence-backed claims the video makes. Shown under the
   *  transcript on its watch page and in llms-full.txt, since the transcript
   *  itself is never edited. */
  sources?: { label: string; url: string }[]
}

export const YOUTUBE_VIDEOS: YouTubeVideo[] = [
  {
    id: 'iYpV-G7rerA',
    title: 'A Closer Look at Pixelmojo: Radar, Vector and Hive',
    kind: 'video',
    slug: 'a-closer-look-at-pixelmojo',
    uploadDate: '2026-09-28T18:14:49-07:00',
    durationSeconds: 145,
    relatedPath: '/about',
    summary:
      'Lloyd Pilapil, founder of Pixelmojo, and Scout, the guide to Radar, take a closer look at Radar, Radar Sites (a visual website builder in development), Vector and Hive.',
    note: 'This is an animated film. The AI answer, website and lead scenes are illustrative examples, not a real client result.',
    transcript:
      "When someone asks AI who to choose, does it understand your business? Lloyd, over here! I'm Lloyd, founder of Pixelmojo. Meet Scout, our guide to Radar. I found something. Come look. This says you only work with big companies. But your website says you help smaller teams too. That's quite a difference. Radar helps you inspect AI answers and the evidence behind them. So we fixed the website and we're done? We make a useful change, then check again. New evidence tells us what to do next. Right, no guessing. And we're building Radar Sites, a visual website builder. Let me get this side. New leads? Send them all to sales. First, find the fit. Vector qualifies incoming leads and routes the next step around your sales process. This one needs a closer look. Hive helps AI co-workers share context and hand work to the right place. Even when a person needs to take over? Especially then. That's Pixelmojo. AI products, visibility, brand and growth. With Vector and Hive, you own the custom application. We maintain the intelligence. But I keep the magnifying glass. Deal. Welcome to Pixelmojo. One more thing. A closer look? Always.",
  },
  {
    id: 'VJ-rZOCSdnk',
    title: 'Meet Pixelmojo: AI Products for Real Business',
    kind: 'video',
    slug: 'meet-pixelmojo',
    uploadDate: '2026-09-27T23:14:33-07:00',
    durationSeconds: 65,
    relatedPath: '/about',
    summary:
      'Lloyd Pilapil, founder of Pixelmojo, introduces Radar, Radar Sites, Vector and Hive in one minute.',
    note: 'The introduction uses AI-generated video. The report on screen is an excerpt from the public Radar sample report with client details removed; product scenes are illustrative.',
    transcript:
      "I'm Lloyd, founder of Pixelmojo. We build AI products for discovery, lead qualification, and business operations. Radar is where many teams start. It helps you inspect how AI describes your business, examine the evidence, and decide what to improve next. Your team makes the changes, then checks what happened. We're also developing Radar Sites, a visual builder for websites. Vector qualifies and routes incoming leads around your sales process, giving your team a clearer next step. Hive coordinates specialized AI co-workers so they can share context, hand off work, and bring in people. With Vector and Hive, you own the custom application. We maintain the intelligence. Welcome to Pixelmojo.",
  },
  {
    id: '6VWeG1Xx4rs',
    title: 'Can AI Tell Your Client Apart? A Real AI Visibility Finding',
    kind: 'video',
    slug: 'can-ai-tell-your-client-apart',
    uploadDate: '2026-09-27T18:50:32-07:00',
    durationSeconds: 84,
    relatedPath: '/platform/sample-report',
    summary:
      'Scout, the guide character in the Radar videos, walks through one real finding from the public Radar sample report: AI engines describing a different, same-named company. It covers the evidence, who owns each step of the fix, and how to recheck.',
    note: 'Scout scenes are illustrative. The evidence is the de-identified July 2026 baseline from the public sample report; the Gemini example still needs client confirmation, and no follow-up result is published.',
    transcript:
      "AI found your client's name. Great. But is it talking about the right business? I'm Scout. Let's untangle one real Radar finding. This public sample report is a de-identified travel agency's July 2026 baseline. It flagged five identity confusion issues. Three marked high severity. Perplexity mixed in similarly named agencies. Gemini described a company profile that still needs the client's confirmation. Those details matter more than a headline score. Start by confirming the business facts with your client. The official name, location, history, and services. Your content owner makes those details clear on the website. Your web team adds matching organization markup. Then align relevant business listings, that's a practical assignment, with people responsible for it. After the update, check the page and its markup. Repeat comparable AI questions. Record the date, providers, prompts, and responses. Does the confusion remain? No follow-up result is published here. A changed answer doesn't prove your edit caused it. Now, the client gets a clear plan, the evidence, the owner, and the next check. One issue becomes a decision your team can explain and act on. That's Radar, the decision layer for AI visibility. I'm Scout, your guide to Radar.",
  },
  {
    id: 'MXEqaWqyE6c',
    title: 'Does AI Get You? Meet Scout, Your Guide to Radar',
    kind: 'video',
    slug: 'meet-scout-your-guide-to-radar',
    uploadDate: '2026-09-27T19:02:52-07:00',
    durationSeconds: 20,
    relatedPath: '/platform',
    summary:
      'Scout asks whether AI gets your business, spots a gap in how AI describes a business, and notes what to check next.',
    note: 'The AI-view and next-check graphics are illustrative examples, not a product screenshot or a measured audit result.',
    transcript:
      "Oh, hey. I'm Scout, your guide to Radar. Let's see how AI understands your business and what to check next.",
  },
  {
    id: 'cvb10u2s4w0',
    title: 'When Buyers Ask AI Who to Choose, Do You Make the Shortlist?',
    kind: 'video',
    slug: 'do-you-make-the-ai-shortlist',
    uploadDate: '2026-09-27T19:45:05-07:00',
    durationSeconds: 18,
    relatedPath: '/platform',
    summary:
      'When buyers ask ChatGPT, Claude, Perplexity and Gemini to compare options on budget, fit and proof, does your business make the shortlist?',
    transcript:
      "When buyers ask AI who to choose, does your business make the shortlist? Huh? Let's take a closer look.",
  },
  {
    id: 'OVGRKA4WFEY',
    title: 'What Should We Fix First? AI Search Visibility for Agencies',
    kind: 'video',
    slug: 'what-should-we-fix-first',
    uploadDate: '2026-09-28T00:29:45-07:00',
    durationSeconds: 20,
    relatedPath: '/platform',
    summary:
      "Scout shows how an agency can answer a client's question, what should we fix first: inspect how AI describes the business, review the evidence, choose one practical change, then check again.",
    note: 'The AI answer, service page and recheck are an illustrative example, not a real client result.',
    transcript:
      "Your client asks, what should we fix first? I'm Scout. Radar helps you inspect how AI describes their business. Review the evidence. Choose one practical change. Then check again, so your client gets a clear next step.",
  },
  {
    id: 'sffxq2uJ1Gg',
    title: 'Website Access vs AI Answers: What Should You Check?',
    kind: 'video',
    slug: 'website-access-vs-ai-answers',
    uploadDate: '2026-09-28T16:09:23-07:00',
    durationSeconds: 30,
    relatedPath: '/platform/sample-report',
    summary:
      "A website loading and an AI answer describing the business are two different things to inspect. Scout explains how to match the next check to the client's question: inspect page access when that is the concern, and read the sampled answer and its prompt to understand the description.",
    note: 'This video uses illustrative diagrams, not a real client result.',
    transcript:
      "The site loads. But what does AI say? A crawl check helps inspect access. A sampled answer shows what a model returned to a question. Those are different observations. Start with the client's question. Check access when that's the concern. Inspect the answer to understand the description. I'm Scout, your guide to Radar.",
  },
  {
    id: 'LMHCsqzSWsc',
    title: 'What Is a Content Graph? Lloyd & Scout Explain',
    kind: 'video',
    slug: 'what-is-a-content-graph',
    uploadDate: '2026-09-28T19:58:33-07:00',
    durationSeconds: 230,
    relatedPath: '/blogs/knowledge-graph-llm-visibility-real-data',
    summary:
      'Lloyd and Scout walk through content graphs: how vector search finds content with a similar meaning, how a graph makes relationships explicit, why both still need good information and evidence, and how Pixelmojo uses both in structured data, Related Reading and answers grounded in relevant content and source URLs.',
    note: 'This is an animated film. Scenes are illustrative.',
    transcript:
      "Lloyd, I found three radars. This one says we should bring an umbrella. Same name. Different thing. Let's find ours. Product pages. Articles. Answers. We've got plenty of content. Now let's show how it connects. That's what a content graph does. Each dot represents something: a product, a topic, a company, or a page. Each connection says how they relate. This article is about Radar. And this Radar is a Pixelmojo product, with its own description and official page. So the line needs to say something. Exactly. About. Mentions. Made by. The relationship matters. That doesn't say Radar. It doesn't have to. Vector search can find content with a similar meaning, even when the words are different. Possible places to look. Right. A similarity score helps rank them. It doesn't prove that a claim is true. And the graph? It makes the relationships explicit. This article is about Radar. Radar is connected to AI visibility. Here's its official page. Now I can follow the connection and inspect the source. Can I add this one? Only if we can support it. Drawing a line doesn't make it true. Graphs need maintenance too. Missing connections leave gaps. Wrong connections can mislead. And vector search also needs good content and checks for weak matches. Both need care. At Pixelmojo, we use both. We define our products, services, topics, and their relationships. Some page connections are set directly, others come from content tags. Where does that show up? In the structured data on our pages, the descriptions machines can read. In Related Reading, which combines similarity with shared entities, and in our question-answering system. How does that last one work? It uses embeddings to find relevant content, including our defined entities. Their descriptions and source URLs help ground the answer. Find something relevant. Then check what it actually says. A buyer wants to know what you do, who you are, and where the evidence is. Your content should make those answers easier. Easy to find and connect. That's the job of the content graph. Yes. And Radar helps us inspect how AI describes a business and where it gets things wrong. We improve the information, then check again. We still check the answer? Always. Clearer information helps. External AI systems still choose their own sources and responses. And this radar? Still the weather.",
    sources: [
      {
        label:
          'Karpukhin et al., "Dense Passage Retrieval for Open-Domain Question Answering" (2020)',
        url: 'https://arxiv.org/abs/2004.04906',
      },
    ],
  },
  {
    id: 'PX5GPSFfkpw',
    title: 'Which Lead Should We Call First? AI Lead Qualification with Vector',
    kind: 'video',
    slug: 'ai-lead-qualification-with-vector',
    uploadDate: '2026-09-28T22:30:58-07:00',
    durationSeconds: 236,
    relatedPath: '/vector',
    summary:
      'Lloyd and Scout walk through how Vector qualifies an inquiry: start from the conversation, keep the evidence and the unknowns visible, apply criteria your business sets, keep the reason beside the decision, and give the seller a useful handoff.',
    note: "This is an animated film. The buyer scenario is illustrative. Vector is Pixelmojo's lead qualification product, not the vector search covered in our content graph video.",
    transcript:
      "This one says urgent. Front of the queue? Maybe. What do they need? It didn't put that on the tag. Inquiries arrive in different ways. A loud request can hide missing details. A quiet one may contain a clear need. So let's open this one. Let's look at the conversation. That's where Vector starts. What problem are they trying to solve? Is there an active project? What is the timing, and who is involved in the decision? This message gives us the problem and the date. Useful evidence. Keep it with the inquiry. And the budget? They haven't told us. That's a question to follow up, not a number to invent. I'll leave it open. Qualification means understanding what we know, what we don't, and what deserves attention next. Start with the customers you can actually help: their problems, requirements, and the situations where your offer fits. So another business might make a different call? Yes. The criteria need to reflect your sales process. Vector is configured around your ideal customer profile, qualification signals, and routing rules. Its assessment should give the receiving team useful context. Can I see why it went that way? The reason belongs beside the decision, with the evidence behind it. Does every inquiry go straight to sales? The next step depends on what we learn. A sales conversation, useful follow-up over time, or a closer look from someone on the team. Each gets a next step. This one doesn't fit the usual pattern. Bring it to a person. Your team can inspect the rationale and change the route. With the original conversation? Yes. Give them enough context to make a considered decision. For the receiving seller, the useful part is the handoff: the need, relevant details, open questions, and why this conversation was routed to them. The inquiry arrives with its story. And the seller can start from there. The build connects qualification to the tools your team uses. We agree the CRM fields, receiving owner, and follow-up workflow. So the next person knows what to do. That's the point. The receiving team helps define the handoff. Vector is a custom build. We establish your baseline, configure the rules, connect the workflow, and test it with your team. And keep checking after launch? Yes. Compare the decisions with real outcomes and adjust where the evidence calls for it. You own the application; Pixelmojo maintains the intelligence platform. What about our urgent one? Check the need. Then choose the next step. The tag can stay. Now it has company.",
  },
  {
    id: 'YS0NBrP-eJw',
    title: 'Why Am I Explaining This Again? AI Agent Handoffs with Hive',
    kind: 'video',
    slug: 'ai-agent-handoffs-with-hive',
    uploadDate: '2026-09-28T22:31:32-07:00',
    durationSeconds: 271,
    relatedPath: '/hive',
    summary:
      'Lloyd and Scout follow one customer request through Hive: specialized agents around shared customer context, handoffs that carry ownership, a person for uncertain cases, visible work, and a staged rollout.',
    note: 'This is an animated film. The customer scenario is illustrative.',
    transcript:
      "I already explained this downstairs. Your request arrived. The explanation didn't. Then we should find out where it went. Same question. Third door. When tools work in separate sessions, the next step can lose the history. Hive starts with that handoff. I'm carrying the case. Why am I carrying the whole conversation too? Let's follow one customer request: they want to change their service before renewal, and there's an unresolved support issue. So the next step needs more than the latest message. Exactly. The history affects what should happen next. Hive is designed to coordinate specialized agents around shared customer context: what's being requested, what happened already, and who owns the next step. The next person can pick up the thread. With access to the information their role needs. One specialist identifies the request. Another checks the relevant account or support history. A coordinator decides which configured workflow should handle the next step. And they pass the history along? The handoff should carry the context and the reason for it. Who has it now? The next owner should be clear. A handoff needs the request, relevant history, the action taken, and anything still unresolved. Otherwise it's just a different inbox. Right. Coordination needs explicit responsibilities. That support issue hasn't been resolved. Can the change go ahead? That part still needs a decision. When the case is uncertain or outside the agreed rules, bring in a person. With the whole relevant thread. So they can review it. I can see the original request, the checks already made, and the question that needs my attention. You don't have to make me tell the whole story again. That's the goal. A useful escalation arrives with context and a clear reason. The team also needs to see what the system did: the handoffs, the rationale, the owner, and the outcome. We can follow the work, too. And stop or change a workflow when the checks show something needs attention. Start with a defined workflow and a small team of agents. Test it, release to a limited group, and compare it with your baseline before expanding. So we learn where the handoffs work. And where people still need to step in. Hive's proposed twelve-week build covers discovery, a small agent team and integrations, staged deployment, then review and handover. What does the customer keep? The custom application, workflows, integrations, and their data environment. Pixelmojo operates the underlying intelligence platform. We define that boundary, along with access and operating controls, before the build. Do I need to explain it again? The next owner has the request, the relevant history, and the open question. Good. This time, the story came with me. That's the handoff we're building for.",
  },
  {
    id: 'xMWNQTo9d5E',
    title: 'Lost in AI Answers? Scout Finds What to Check Next',
    kind: 'video',
    slug: 'lost-in-ai-answers',
    uploadDate: '2026-09-30T03:49:14-07:00',
    durationSeconds: 55,
    relatedPath: '/platform',
    summary:
      'When AI answers about a business point in different directions, Scout follows the clue, checks the evidence and picks one practical next check. A 55-second animated teaser for Radar, with sound effects only.',
    note: 'This is an animated film with sound effects only. The services page and next-check cards are illustrative concepts, not a real client result or report.',
    transcript:
      '[Waves. A map rustles.] Lost in AI answers? [Wooden signs clack. Footsteps.] [A parachute opens. Wind. Birds flutter and chirp.] [Goggles snap. A quick landing. Splash.] [Bubbles. A pufferfish puffs.] Check the evidence. (Illustrative concept.) [Paper flicks. A click.] Next check: clarify your services. (Illustrative concept.) [A folder slides over. A chime.] Know what to check next. [A bird chirps. An elastic pop.] The decision layer for AI visibility. Radar by Pixelmojo.',
  },
  {
    id: 'XBx852xIrmM',
    title: 'Does AI Get Your Business? Meet Scout, Your Guide to Radar #Shorts',
    kind: 'short',
    shortOf: 'MXEqaWqyE6c',
    uploadDate: '2026-09-27T19:06:59-07:00',
    durationSeconds: 20,
    relatedPath: '/platform',
  },
  {
    id: 'cyagQl7pQJ4',
    title:
      'Same Name, Different Business: Can AI Tell Your Client Apart? #Shorts',
    kind: 'short',
    shortOf: '6VWeG1Xx4rs',
    uploadDate: '2026-09-27T19:47:03-07:00',
    durationSeconds: 84,
    relatedPath: '/platform/sample-report',
  },
  {
    id: '_VH6s23xNaM',
    title: 'What Should We Fix First? Radar for Agencies #Shorts',
    kind: 'short',
    shortOf: 'OVGRKA4WFEY',
    uploadDate: '2026-09-28T00:31:31-07:00',
    durationSeconds: 20,
    relatedPath: '/platform',
  },
  {
    id: '5iYj83HzT3Y',
    title: 'The Site Loads. What Does AI Say? #Shorts',
    kind: 'short',
    shortOf: 'sffxq2uJ1Gg',
    uploadDate: '2026-09-28T16:11:03-07:00',
    durationSeconds: 30,
    relatedPath: '/platform/sample-report',
  },
  {
    id: 'sbDwnInvfkI',
    title: "Lost in AI Answers? Scout's Next Move #Shorts",
    kind: 'short',
    shortOf: 'xMWNQTo9d5E',
    uploadDate: '2026-09-30T03:51:37-07:00',
    durationSeconds: 55,
    relatedPath: '/platform',
  },
]

export const youtubeWatchUrl = (id: string) =>
  `https://www.youtube.com/watch?v=${id}`

/** Privacy-mode player. Only this host is allowed in the CSP frame-src. */
export const youtubeEmbedUrl = (id: string) =>
  `https://www.youtube-nocookie.com/embed/${id}`

export const youtubeThumbnailUrl = (id: string) =>
  `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`

/** 84 → "1:24" */
export const formatVideoLength = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

/** 84 → "PT1M24S" (schema.org duration) */
export const isoVideoDuration = (seconds: number) =>
  `PT${Math.floor(seconds / 60) ? `${Math.floor(seconds / 60)}M` : ''}${seconds % 60 ? `${seconds % 60}S` : ''}`

export function getYouTubeVideo(id: string): YouTubeVideo {
  const video = YOUTUBE_VIDEOS.find(v => v.id === id)
  if (!video) throw new Error(`Unknown YouTube video: ${id}`)
  return video
}

/** A long-form video: each one has a page on the site, a summary and a
 *  transcript. */
export type LongFormVideo = YouTubeVideo & {
  kind: 'video'
  slug: string
  summary: string
  transcript: string
}

function isLongForm(v: YouTubeVideo): v is LongFormVideo {
  if (v.kind !== 'video') return false
  if (!v.slug || !v.summary || !v.transcript) {
    throw new Error(
      `Long-form video ${v.id} needs a slug, a summary and a transcript`
    )
  }
  return true
}

export const LONG_FORM_VIDEOS: LongFormVideo[] =
  YOUTUBE_VIDEOS.filter(isLongForm)

/** YouTube's upload date as shown on YouTube: the calendar date stored in
 *  `uploadDate`, never shifted into another time zone. */
export const formatUploadDate = (uploadDate: string) =>
  new Date(`${uploadDate.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })

/** A long-form video's page on the site. */
export const videoPagePath = (v: Pick<LongFormVideo, 'slug'>) =>
  `/videos/${v.slug}`

export const getVideoBySlug = (slug: string): LongFormVideo | undefined =>
  LONG_FORM_VIDEOS.find(v => v.slug === slug)

/** Link text for each `relatedPath` on a video's page. youtube-videos.test.ts
 *  fails when a long-form video's relatedPath has no label here. */
export const RELATED_PAGE_LABELS: Record<string, string> = {
  '/about': 'About Pixelmojo',
  '/platform': 'Radar',
  '/platform/sample-report': 'The Radar sample report',
  '/vector': 'Vector',
  '/hive': 'Hive',
  '/blogs/knowledge-graph-llm-visibility-real-data':
    'How we built a knowledge graph that LLMs cite',
}

/** Groups a one-line transcript into short paragraphs for reading, without
 *  dropping or changing any text. */
export function transcriptParagraphs(text: string, sentencesPer = 5): string[] {
  // A [bracketed sound cue] is one unit, however many full stops it holds.
  // A sentence ends at its punctuation, just before a cue, or at the end of
  // the text (a final fragment without punctuation is kept).
  const sentences = text.match(
    /\[[^\]]*\]["')]*\s*|[^.!?[]+(?:[.!?]+["')\]]*|(?=\[)|$)\s*/g
  ) ?? [text]
  const paragraphs: string[] = []
  for (let i = 0; i < sentences.length; i += sentencesPer) {
    paragraphs.push(
      sentences
        .slice(i, i + sentencesPer)
        .join('')
        .trim()
    )
  }
  return paragraphs
}

/**
 * Last content revision of the /videos pages (their sitemap lastmod). Move it
 * forward only with an edit to what those pages show: a video added, or a
 * title, summary, note or transcript changed.
 */
export const VIDEO_PAGES_UPDATED = '2026-09-30'

export const shortsOf = (id: string) =>
  YOUTUBE_VIDEOS.filter(v => v.shortOf === id)

/** The VideoObject input for AutoSchema's `videos` / `buildVideoSchemas`.
 *  A long-form video carries its watch page as `url` and `<page>#video` as
 *  its `@id`, so every embed of it across the site is one video node. */
export const toVideoItem = (v: YouTubeVideo): VideoItem => ({
  ...(v.kind === 'video' && v.slug
    ? {
        '@id': `${videoPagePath({ slug: v.slug })}#video`,
        url: videoPagePath({ slug: v.slug }),
      }
    : {}),
  name: v.title,
  description: v.summary,
  thumbnailUrl: youtubeThumbnailUrl(v.id),
  uploadDate: v.uploadDate,
  embedUrl: youtubeEmbedUrl(v.id),
  duration: isoVideoDuration(v.durationSeconds),
})
